import React, { useState, useMemo } from "react";
import { SectionBasedProduct } from "@brandclub/common-ui";
import { ConciergeOrderData } from "../types/conversation";
import { formatRelativeDate } from "../utils/dateFormatter";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

interface OrderWidgetProps {
  order: ConciergeOrderData;
  productMetadataMap: Map<string, SectionBasedProduct>;
}

const StatusBadge: React.FC<{ status: ConciergeOrderData["shipmentStatus"] }> = ({
  status,
}) => {
  const statusConfig = {
    DELIVERED: {
      label: "Delivered",
      className: "tw:bg-emerald-100 tw:text-emerald-700",
    },
    CANCELLED: {
      label: "Cancelled",
      className: "tw:bg-red-100 tw:text-red-700",
    },
    IN_TRANSIT: {
      label: "In transit",
      className: "tw:bg-blue-100 tw:text-blue-700",
    },
    PROCESSING: {
      label: "Processing",
      className: "tw:bg-amber-100 tw:text-amber-700",
    },
    UNKNOWN: {
      label: "Processing",
      className: "tw:bg-amber-100 tw:text-amber-700",
    },
  };

  const config = statusConfig[status] || statusConfig.UNKNOWN;

  return (
    <span
      className={`tw:inline-flex tw:items-center tw:rounded-full tw:px-2.5 tw:py-0.5 tw:text-xs tw:font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
};

export const OrderWidget: React.FC<OrderWidgetProps> = ({
  order,
  productMetadataMap,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get product details from metadata map
  const orderProducts = useMemo(() => {
    return order.products.map((orderProduct) => {
      const lookupSku = orderProduct.stacklineSku || orderProduct.retailerSku;
      const product = lookupSku
        ? productMetadataMap.get(lookupSku)
        : undefined;
      return {
        ...orderProduct,
        product,
      };
    });
  }, [order.products, productMetadataMap]);

  // Calculate totals
  const totalItems = useMemo(() => {
    return orderProducts.reduce((sum, item) => sum + item.quantity, 0);
  }, [orderProducts]);

  const totalPrice = useMemo(() => {
    return orderProducts.reduce(
      (sum, item) => sum + item.retailPrice * item.quantity,
      0
    );
  }, [orderProducts]);

  // Get primary product image (first product with an image)
  const primaryImage = useMemo(() => {
    const firstProduct = orderProducts.find(
      (item) => item.product?.imageUrls?.[0]
    );
    return firstProduct?.product?.imageUrls?.[0];
  }, [orderProducts]);

  // Format date
  const { relative: relativeDate, absolute: absoluteDate } = formatRelativeDate(
    order.orderDate
  );

  // Get retailer display name
  const retailerDisplayName = order.retailerDisplayName || order.retailerName;

  // Generate summary text
  const summaryText =
    totalItems === 1
      ? orderProducts[0]?.product?.title || "1 item"
      : `${totalItems} items`;

  return (
    <div className="tw:relative tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:shadow-sm tw:transition-shadow hover:tw:shadow-md">
      {/* Status badge - top right */}
      <div className="tw:absolute tw:right-3 tw:top-3 tw:z-10">
        <StatusBadge status={order.shipmentStatus} />
      </div>

      {/* Collapsed view */}
      <div
        className="tw:flex tw:gap-4 tw:p-4 tw:cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} order details`}
      >
        {/* Product image */}
        <div className="tw:flex-shrink-0">
          {primaryImage ? (
            <img
              src={primaryImage}
              alt={summaryText}
              className="tw:h-16 tw:w-16 tw:rounded-lg tw:object-cover"
            />
          ) : (
            <div className="tw:h-16 tw:w-16 tw:rounded-lg tw:bg-gray-100 tw:flex tw:items-center tw:justify-center">
              <span className="tw:text-gray-400 tw:text-xs">No image</span>
            </div>
          )}
        </div>

        {/* Order info */}
        <div className="tw:flex-1 tw:min-w-0">
          <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
            <div className="tw:flex-1 tw:min-w-0">
              <p className="tw:text-sm tw:font-medium tw:text-gray-900 tw:truncate">
                {summaryText}
              </p>
              <p className="tw:text-sm tw:text-gray-500 tw:mt-0.5">
                from {retailerDisplayName}
              </p>
              <p
                className="tw:text-xs tw:text-gray-400 tw:mt-1"
                title={absoluteDate}
              >
                {relativeDate}
              </p>
            </div>
            <div className="tw:flex tw:flex-col tw:items-end tw:gap-1">
              <p className="tw:text-sm tw:font-semibold tw:text-gray-900">
                ${totalPrice.toFixed(2)}
              </p>
              <button
                className="tw:text-gray-400 hover:tw:text-gray-600 tw:transition-colors"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronUpIcon className="tw:h-4 tw:w-4" />
                ) : (
                  <ChevronDownIcon className="tw:h-4 tw:w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded view */}
      {isExpanded && (
        <div className="tw:border-t tw:border-gray-200 tw:px-4 tw:py-4 tw:space-y-4">
          {/* Product list */}
          <div className="tw:space-y-3">
            {orderProducts.map((item, index) => {
              const product = item.product;
              const imageUrl = product?.imageUrls?.[0];
              const title = product?.title || "Unknown Product";
              const brandName = product?.brandName;

              return (
                <div key={index} className="tw:flex tw:gap-3">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={title}
                      className="tw:h-16 tw:w-16 tw:rounded tw:object-cover tw:flex-shrink-0"
                    />
                  ) : (
                    <div className="tw:h-16 tw:w-16 tw:rounded tw:bg-gray-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0">
                      <span className="tw:text-gray-400 tw:text-xs">
                        No image
                      </span>
                    </div>
                  )}
                  <div className="tw:flex-1 tw:min-w-0">
                    <p className="tw:text-sm tw:font-medium tw:text-gray-900 tw:line-clamp-2">
                      {title}
                    </p>
                    {brandName && (
                      <p className="tw:text-xs tw:text-gray-500 tw:mt-0.5">
                        {brandName}
                      </p>
                    )}
                    {product?.categoryName && (
                      <p className="tw:text-xs tw:text-gray-400 tw:mt-0.5">
                        {product.categoryName}
                        {product.subCategoryName &&
                          ` • ${product.subCategoryName}`}
                      </p>
                    )}
                    <div className="tw:flex tw:items-center tw:gap-2 tw:mt-1">
                      <p className="tw:text-sm tw:text-gray-600">
                        ${item.retailPrice.toFixed(2)} × {item.quantity}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order details */}
          <div className="tw:pt-3 tw:border-t tw:border-gray-100 tw:space-y-1">
            <div className="tw:flex tw:justify-between tw:text-xs tw:text-gray-500">
              <span>Order ID:</span>
              <span className="tw:font-mono">{order.orderId}</span>
            </div>
            {order.orderType && (
              <div className="tw:flex tw:justify-between tw:text-xs tw:text-gray-500">
                <span>Order Type:</span>
                <span className="tw:capitalize">
                  {order.orderType.toLowerCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
